/**
 * MCF Value Extraction Engine
 * 
 * Converts raw user assessment responses into 0-100 MCF scores using three patterns:
 * - Pattern A (option_score): Categorical questions with per-option score values
 * - Pattern B (scoring_tiers): Numeric/percentage questions with min/max tier ranges
 * - Pattern C (formula): Inverse or calculated formulas like "100 - user_response"
 */

import type { Json } from '@/integrations/supabase/types';

// ─── Type definitions ───────────────────────────────────────────────

interface OptionItem {
  value: string;
  label: string;
  score: number;
}

interface ScoringTier {
  min?: number;
  max?: number;
  range?: string;
  score: number;
}

type QuestionOptions =
  | OptionItem[]
  | { options: OptionItem[] }
  | { scoring_tiers: ScoringTier[] };

type ResponseValue = string | number | string[] | { value: string };

// ─── Internal helpers ───────────────────────────────────────────────

/** Narrow a Json-typed DB value to the engine's ResponseValue union. */
function narrowResponseValue(raw: Json): ResponseValue | null {
  if (typeof raw === 'string' || typeof raw === 'number') return raw;
  if (Array.isArray(raw) && raw.every(v => typeof v === 'string')) return raw as string[];
  if (typeof raw === 'object' && raw !== null && 'value' in raw && typeof (raw as Record<string, unknown>).value === 'string') {
    return raw as unknown as { value: string };
  }
  return null;
}

// ─── Pattern A: Option-level score lookup ────────────────────────────

function extractOptionScore(responseValue: ResponseValue, questionOptions: QuestionOptions): number | null {
  const selectedValue = typeof responseValue === 'string'
    ? responseValue
    : (responseValue as { value: string })?.value ?? responseValue;

  if (!selectedValue) return null;

  // Handle both formats:
  // Format 1: plain array [{value, label, score}, ...]
  // Format 2: wrapped {options: [{value, label, score}, ...]}
  const optionsArray = Array.isArray(questionOptions)
    ? questionOptions
    : (questionOptions as { options: OptionItem[] })?.options;

  if (!optionsArray || !Array.isArray(optionsArray)) return null;

  const matchingOption = optionsArray.find(
    (opt: OptionItem) => opt.value === selectedValue || opt.value === String(selectedValue)
  );

  return matchingOption?.score ?? null;
}

// ─── Pattern B: Tier-based scoring ──────────────────────────────────

function extractTierScore(responseValue: ResponseValue, questionOptions: QuestionOptions): number | null {
  const numericValue = typeof responseValue === 'number'
    ? responseValue
    : parseFloat(responseValue as string);

  if (isNaN(numericValue)) return null;

  // Handle scoring_tiers at top level or nested
  const opts = questionOptions as Record<string, unknown>;
  const tiers = (opts?.scoring_tiers as ScoringTier[] | undefined)
    || (typeof opts === 'object' && !Array.isArray(opts) ? opts?.scoring_tiers as ScoringTier[] | undefined : null);

  if (!tiers || !Array.isArray(tiers)) return null;

  const matchingTier = tiers.find((tier: ScoringTier) => {
    // Format 1: {min, max, score}
    if (tier.min !== undefined && tier.max !== undefined) {
      return numericValue >= tier.min && numericValue <= tier.max;
    }
    // Format 2: {range: "90-100", score}
    if (tier.range) {
      const parts = tier.range.split('-').map(Number);
      if (parts.length === 2 && !isNaN(parts[0]) && !isNaN(parts[1])) {
        return numericValue >= parts[0] && numericValue <= parts[1];
      }
    }
    return false;
  });

  return matchingTier?.score ?? null;
}

// ─── Pattern C: Formula-based scoring ───────────────────────────────

function extractFormulaScore(responseValue: ResponseValue, calculationFormula: string | null): number | null {
  const numericValue = typeof responseValue === 'number'
    ? responseValue
    : parseFloat(responseValue as string);

  if (isNaN(numericValue)) return null;
  if (!calculationFormula) return numericValue;

  if (calculationFormula.includes('100') && calculationFormula.includes('user_response')) {
    return 100 - numericValue;
  }

  console.warn(`[Scoring] Unknown formula: ${calculationFormula}`);
  return numericValue;
}

// ─── Pattern D: Multi-select count scoring ──────────────────────────

function extractMultiSelectCountScore(responseValue: ResponseValue, tiers: Array<{ min: number; score: number }>): number {
  let items: unknown[];
  try {
    const parsed = typeof responseValue === 'string' ? JSON.parse(responseValue) : responseValue;
    items = Array.isArray(parsed) ? parsed : [];
  } catch {
    return 0;
  }

  const count = items.length;
  if (count === 0) return 0;

  // Tiers expected descending by min; find first where count >= min
  const sorted = [...tiers].sort((a, b) => b.min - a.min);
  const match = sorted.find(t => count >= t.min);
  return match?.score ?? 0;
}

// ─── Main extraction router ─────────────────────────────────────────

/**
 * Main extraction router - determines which scoring pattern to apply
 * and returns a 0-100 clamped score.
 *
 * Accepts Json from DB at the boundary, narrows to ResponseValue internally.
 */
export function extractMcfValue(
  rawResponseValue: Json,
  sourceType: string,
  calculationFormula: string | null,
  questionOptions: QuestionOptions | null,
  transformLogic?: { type?: string; tiers?: Array<{ min: number; score: number }>; scoring_tiers?: Array<{ min: number; score: number }> } | null
): { score: number | null; calculationMethod: string } {
  let score: number | null = null;
  let calculationMethod = 'unknown';

  const responseValue = narrowResponseValue(rawResponseValue);
  if (responseValue === null && sourceType !== 'calculated') {
    return { score: null, calculationMethod: 'unrecognized_response' };
  }

  const resolvedTiers = transformLogic?.tiers || transformLogic?.scoring_tiers;

  if (transformLogic?.type === 'allocation_extract' && resolvedTiers && responseValue !== null) {
    calculationMethod = 'allocation_extract';
    const numVal = typeof responseValue === 'number' ? responseValue : parseFloat(responseValue as string);
    if (!isNaN(numVal)) {
      const sorted = [...resolvedTiers].sort((a, b) => b.min - a.min);
      score = sorted.find(t => numVal >= t.min)?.score ?? 0;
    }

  } else if (transformLogic?.type === 'multi_select_count' && resolvedTiers && responseValue !== null) {
    calculationMethod = 'multi_select_count';
    score = extractMultiSelectCountScore(responseValue, resolvedTiers);

  } else if (transformLogic?.type === 'scoring_tiers' && resolvedTiers && responseValue !== null) {
    calculationMethod = 'scoring_tiers';
    const numVal = typeof responseValue === 'number' ? responseValue : parseFloat(responseValue as string);
    if (!isNaN(numVal)) {
      // Support both min-only (descending match) and min+max (range match)
      const hasMaxValues = resolvedTiers.some((t: any) => t.max !== undefined);
      if (hasMaxValues) {
        const match = resolvedTiers.find((t: any) => numVal >= t.min && numVal <= t.max);
        score = match?.score ?? 0;
      } else {
        const sorted = [...resolvedTiers].sort((a, b) => b.min - a.min);
        score = sorted.find(t => numVal >= t.min)?.score ?? 0;
      }
    }

  } else if (sourceType === 'calculated') {
    calculationMethod = 'calculated';
    score = null;

  } else if (calculationFormula && responseValue !== null) {
    calculationMethod = 'formula';
    score = extractFormulaScore(responseValue, calculationFormula);

  } else if (questionOptions && responseValue !== null) {
    const hasTopLevelTiers = !Array.isArray(questionOptions) && 'scoring_tiers' in questionOptions;
    const hasNestedTiers = Array.isArray(questionOptions) && questionOptions.length > 0 &&
      typeof questionOptions[0] === 'object' && 'scoring_tiers' in (questionOptions[0] as unknown as Record<string, unknown>);

    if (hasTopLevelTiers || hasNestedTiers) {
      calculationMethod = 'scoring_tiers';
      const effectiveOptions: QuestionOptions = hasTopLevelTiers
        ? questionOptions
        : { scoring_tiers: ((questionOptions as OptionItem[])[0] as unknown as { scoring_tiers: ScoringTier[] }).scoring_tiers };
      score = extractTierScore(responseValue, effectiveOptions);

    } else if (Array.isArray(questionOptions) || ('options' in questionOptions)) {
      calculationMethod = 'option_score';
      score = extractOptionScore(responseValue, questionOptions);
    }
  }

  // Clamp to 0-100
  if (score !== null) {
    score = Math.max(0, Math.min(100, score));
  }

  return { score, calculationMethod };
}
